import { useCallback, useEffect, useMemo, useState } from "react";
import { CircleDollarSign, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../../api/axiosClient";
import PageHeader from "../../../components/common/PageHeader";
import { formatMessageRate } from "../../../utils/currencyFormatters";

const ENDPOINT = "/admin/billing/meta-rate-cards";
const CATEGORY_OPTIONS = ["marketing", "utility", "authentication", "service"];

const initialForm = {
  countryCode: "IN",
  category: "marketing",
  metaCost: "",
  currency: "INR",
  effectiveFromUtc: "",
  effectiveToUtc: "",
  isActive: true,
  sourceVersion: "manual-admin",
};

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.errorMessage ||
    error?.message ||
    fallbackMessage
  );
}

function extractList(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.payload)) return response.data.payload;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
}

function formatRate(value, currency = "INR") {
  if (formatMessageRate) {
    return formatMessageRate(value, currency, { compact: true });
  }

  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(amount);
  } catch {
    return `₹${amount.toFixed(2)}`;
  }
}

function formatDateTime(value) {
  if (!value) return "Open ended";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid date";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function toInputDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromInputDateTime(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export default function MetaRateCardsPage() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    countryCode: "",
    category: "",
    includeInactive: false,
  });
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState("");

  const requestConfig = useMemo(
    () => ({
      __skipBizHeader: true,
    }),
    []
  );

  const loadRateCards = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError("");

      const response = await axiosClient.get(ENDPOINT, {
        ...requestConfig,
        params: {
          countryCode: filters.countryCode.trim() || undefined,
          category: filters.category || undefined,
          includeInactive: filters.includeInactive,
        },
      });

      setRows(extractList(response));
    } catch (error) {
      const message = getErrorMessage(error, "Failed to load Meta rate cards.");
      setRows([]);
      setLoadError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [filters.category, filters.countryCode, filters.includeInactive, requestConfig]);

  useEffect(() => {
    loadRateCards();
  }, [loadRateCards]);

  const validate = () => {
    const nextErrors = {};

    if (!form.countryCode.trim()) {
      nextErrors.countryCode = "Country code is required.";
    }

    if (!form.category.trim()) {
      nextErrors.category = "Category is required.";
    }

    if (form.metaCost === "" || Number.isNaN(Number(form.metaCost))) {
      nextErrors.metaCost = "Enter a valid Meta cost.";
    } else if (Number(form.metaCost) < 0) {
      nextErrors.metaCost = "Meta cost cannot be negative.";
    }

    if (!form.currency.trim()) {
      nextErrors.currency = "Currency is required.";
    }

    if (!form.effectiveFromUtc) {
      nextErrors.effectiveFromUtc = "Effective from is required.";
    }

    const from = fromInputDateTime(form.effectiveFromUtc);
    const to = fromInputDateTime(form.effectiveToUtc);
    if (form.effectiveFromUtc && !from) {
      nextErrors.effectiveFromUtc = "Enter a valid effective from date.";
    }
    if (form.effectiveToUtc && !to) {
      nextErrors.effectiveToUtc = "Enter a valid effective to date.";
    }
    if (from && to && new Date(to) < new Date(from)) {
      nextErrors.effectiveToUtc = "Effective to must be after effective from.";
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId("");
    setFieldErrors({});
    setSubmitError("");
  };

  const handleFormChange = event => {
    const { name, value, type, checked } = event.target;
    setForm(current => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setFieldErrors(current => ({ ...current, [name]: "" }));
    setSubmitError("");
  };

  const handleEdit = row => {
    setEditingId(row.id);
    setForm({
      countryCode: row.countryCode || "IN",
      category: row.category || "marketing",
      metaCost: row.metaCost ?? "",
      currency: row.currency || "INR",
      effectiveFromUtc: toInputDateTime(row.effectiveFromUtc),
      effectiveToUtc: toInputDateTime(row.effectiveToUtc),
      isActive: row.isActive !== false,
      sourceVersion: row.sourceVersion || "manual-admin",
    });
    setFieldErrors({});
    setSubmitError("");
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      countryCode: form.countryCode.trim().toUpperCase(),
      category: form.category.trim().toLowerCase(),
      metaCost: Number(form.metaCost),
      currency: form.currency.trim().toUpperCase(),
      effectiveFromUtc: fromInputDateTime(form.effectiveFromUtc),
      effectiveToUtc: fromInputDateTime(form.effectiveToUtc),
      isActive: form.isActive,
      sourceVersion: form.sourceVersion.trim() || "manual-admin",
    };

    try {
      setIsSaving(true);
      setSubmitError("");

      if (editingId) {
        await axiosClient.put(`${ENDPOINT}/${editingId}`, payload, requestConfig);
        toast.success("Meta rate card updated");
      } else {
        await axiosClient.post(ENDPOINT, payload, requestConfig);
        toast.success("Meta rate card created");
      }

      resetForm();
      await loadRateCards();
    } catch (error) {
      const message = getErrorMessage(error, "Failed to save Meta rate card.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async row => {
    try {
      setDeactivatingId(row.id);
      await axiosClient.post(`${ENDPOINT}/${row.id}/deactivate`, null, requestConfig);
      toast.success("Meta rate card deactivated");
      await loadRateCards();
      if (editingId === row.id) {
        resetForm();
      }
    } catch (error) {
      const message = getErrorMessage(error, "Failed to deactivate Meta rate card.");
      toast.error(message);
    } finally {
      setDeactivatingId("");
    }
  };

  const renderInput = (name, label, type = "text", wrapperClassName = "") => (
    <label className={`space-y-1 ${wrapperClassName}`}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={handleFormChange}
        step={type === "number" ? "0.000001" : undefined}
        min={type === "number" ? "0" : undefined}
        className={[
          "w-full rounded-[10px] border bg-white px-3 py-2 text-sm text-slate-700 outline-none transition",
          fieldErrors[name]
            ? "border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
            : "border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100",
        ].join(" ")}
      />
      {fieldErrors[name] ? (
        <div className="text-xs font-medium text-rose-600">{fieldErrors[name]}</div>
      ) : null}
    </label>
  );

  return (
    <div className="min-h-screen bg-[#f3f4f6] py-6">
      <section className="px-4">
        <div className="mx-auto max-w-[1240px] space-y-6">
          <div className="px-4 pt-4 pb-1">
            <PageHeader
              icon={CircleDollarSign}
              title="Meta Rate Cards"
              subtitle="Manage Meta base prices used by the WhatsApp pricing resolver."
            />
          </div>

          <div className="grid gap-5 px-4">
            <div className="rounded-[10px] border border-slate-200 bg-white shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-4 px-4 py-4">
                <div>
                  <div className="text-sm font-semibold text-slate-800">
                    {editingId ? "Edit Rate Card" : "Create Rate Card"}
                  </div>
                  <div className="mt-0.5 text-xs leading-5 text-slate-500">
                    Store the upstream base cost by country, category, and effective window.
                  </div>
                </div>

                {submitError ? (
                  <div className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {submitError}
                  </div>
                ) : null}

                <div className="grid gap-3 md:grid-cols-2">
                  {renderInput("countryCode", "Country Code")}

                  <label className="space-y-1">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      Category
                    </span>
                    <select
                      name="category"
                      value={form.category}
                      onChange={handleFormChange}
                      className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    >
                      {CATEGORY_OPTIONS.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.category ? (
                      <div className="text-xs font-medium text-rose-600">
                        {fieldErrors.category}
                      </div>
                    ) : null}
                  </label>

                  {renderInput("metaCost", "Meta Cost", "number")}
                  {renderInput("currency", "Currency")}
                  {renderInput("effectiveFromUtc", "Effective From", "datetime-local")}
                  {renderInput("effectiveToUtc", "Effective To", "datetime-local")}
                  {renderInput("sourceVersion", "Source Version", "text", "md:col-span-2")}
                </div>

                <label className="flex items-center gap-2 rounded-[10px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleFormChange}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  Active
                </label>

                <div className="flex flex-wrap justify-end gap-2">
                  {editingId ? (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="rounded-[10px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  ) : null}
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 rounded-[10px] bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {editingId ? "Save Changes" : "Create Rate Card"}
                  </button>
                </div>
              </form>
            </div>

            <div className="rounded-[10px] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto_auto]">
                  <input
                    value={filters.countryCode}
                    onChange={event =>
                      setFilters(current => ({
                        ...current,
                        countryCode: event.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="Country code"
                    className="rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  />
                  <select
                    value={filters.category}
                    onChange={event =>
                      setFilters(current => ({
                        ...current,
                        category: event.target.value,
                      }))
                    }
                    className="rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">All categories</option>
                    {CATEGORY_OPTIONS.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <label className="inline-flex items-center gap-2 rounded-[10px] border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={filters.includeInactive}
                      onChange={event =>
                        setFilters(current => ({
                          ...current,
                          includeInactive: event.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Include inactive
                  </label>
                  <button
                    type="button"
                    onClick={loadRateCards}
                    disabled={isLoading}
                    className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <RefreshCcw className={isLoading ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
                    Refresh
                  </button>
                </div>

                {loadError ? (
                  <div className="mt-4 rounded-[10px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {loadError}
                  </div>
                ) : null}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                    <tr>
                      <th className="px-5 py-3">Country</th>
                      <th className="px-5 py-3">Category</th>
                      <th className="px-5 py-3">Meta Cost</th>
                      <th className="px-5 py-3">Effective</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Source</th>
                      <th className="px-5 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={7} className="px-5 py-10 text-center text-slate-500">
                          <div className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                            Loading Meta rate cards...
                          </div>
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-5 py-10 text-center text-slate-500">
                          No Meta rate cards available
                        </td>
                      </tr>
                    ) : (
                      rows.map(row => (
                        <tr key={row.id} className="align-top">
                          <td className="px-5 py-4 font-semibold text-slate-700">
                            {row.countryCode || "-"}
                          </td>
                          <td className="px-5 py-4 capitalize text-slate-700">
                            {row.category || "-"}
                          </td>
                          <td className="min-w-[130px] whitespace-nowrap px-5 py-4 text-sm font-semibold tabular-nums text-slate-900">
                            {formatRate(row.metaCost, row.currency)}
                          </td>
                          <td className="min-w-[190px] px-5 py-4 text-slate-600">
                            <div>{formatDateTime(row.effectiveFromUtc)}</div>
                            <div className="mt-1 text-xs text-slate-400">
                              To: {formatDateTime(row.effectiveToUtc)}
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span
                              className={[
                                "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                                row.isActive
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-slate-100 text-slate-500",
                              ].join(" ")}
                            >
                              {row.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="max-w-[220px] break-words px-5 py-4 text-slate-600">
                            {row.sourceVersion || "-"}
                          </td>
                          <td className="min-w-[180px] px-5 py-4 text-right">
                            <div className="flex flex-wrap justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => handleEdit(row)}
                                className="rounded-[10px] border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                              >
                                Edit
                              </button>
                              {row.isActive ? (
                                <button
                                  type="button"
                                  onClick={() => handleDeactivate(row)}
                                  disabled={deactivatingId === row.id}
                                  className="rounded-[10px] border border-rose-200 px-2.5 py-1.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                  {deactivatingId === row.id ? "Deactivating..." : "Deactivate"}
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
